import { useContext, useState } from 'react'
import { AuthContext } from '@/context/auth-context'
import { useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { useCreateNewChat } from '@/chat-common/new_chat/hooks/useCreateNewChat'
import { selectUnsentAgentV2Chat } from '@/agent-v2/conversations/store/selectors'

export type NewChatMatterBoundAction = {
  /** Click handler for the "New Chat" trigger — creates a chat in the active matter. */
  handleButtonClick: () => void
  /** The viewer's unsent chat in the active matter, which the trigger returns to instead of creating a new one. */
  unsentChatId: string | null
  /** Whether the trigger should be disabled (the matter is loading or a create request is in flight). */
  clickDisabled: boolean
  /** Whether the most recent create attempt failed. */
  createError: boolean
}

export function useNewChatMatterBoundAction(onComplete?: () => void): NewChatMatterBoundAction {
  const authContext = useContext(AuthContext)

  const { trackEvent } = useAnalytics()
  const { createNewChat } = useCreateNewChat()

  const [clickDisabled, setClickDisabled] = useState<boolean>(false)
  const [createError, setCreateError] = useState<boolean>(false)

  const activeMatterId = useAppSelector((state: RootState) => state.mattersState.activeMatterId)
  const unsentChatId = useAppSelector(
    (state) => selectUnsentAgentV2Chat(state, { matterId: activeMatterId, currentUserId: authContext?.user?.uid })?.id ?? null
  )

  const handleButtonClick = () => {
    if (!activeMatterId || clickDisabled) return
    const matterId = activeMatterId

    setClickDisabled(true)
    setCreateError(false)

    function onCreateSuccess(created: boolean) {
      setClickDisabled(false)

      if (created) {
        trackEvent(AnalyticsEvent.NewChat, {
          conversationMatterId: matterId,
          skill: '',
          skillSource: '',
        })
      }

      onComplete?.()
    }

    function onCreateError(e: unknown) {
      console.error('Error creating a new chat:', e)
      setCreateError(true)
      setClickDisabled(false)
    }

    createNewChat(true, matterId, onCreateSuccess, onCreateError)
  }

  return {
    handleButtonClick,
    unsentChatId,
    clickDisabled: clickDisabled || !activeMatterId,
    createError,
  }
}
